/**
 * Cycle Readiness Validation
 * 
 * Validates that a cycle is properly configured before phase transitions.
 * Part of Workstream 4: Cycle Boundary Clarity
 */

import { isFeatureEnabled, FEATURE_FLAGS } from './featureFlags';

/**
 * Readiness check section
 */
export interface ReadinessSection {
  ready: boolean;
  items: ReadinessItem[];
  gaps: string[];
}

/**
 * Individual readiness item
 */
export interface ReadinessItem {
  id: string;
  label: string;
  status: 'complete' | 'incomplete' | 'warning' | 'not_applicable';
  message?: string;
  actionUrl?: string;
}

/**
 * Participants readiness
 */
export interface ParticipantsReadiness extends ReadinessSection {
  facilitatorAssigned: boolean;
  facilitatorName?: string;
  criticalStakeholdersIdentified: boolean;
  stakeholderCount: number;
  participantCount: number;
  roleAssignments: Array<{
    role: string;
    assignedCount: number;
    requiredCount: number;
  }>;
}

/**
 * Clarity plan readiness
 */
export interface ClarityPlanReadiness extends ReadinessSection {
  comparisonSetDefined: boolean;
  comparisonSetSize: number;
  criteriaSelected: boolean;
  criteriaCount: number;
  weightsAssigned: boolean;
  weightsComplete: boolean;
  sessionsScheduled: boolean;
  sessionCount: number;
  toolsSelected: boolean;
  toolCount: number;
  itemsWithMissingValues: number;
  itemsReady: number;
  totalItems: number;
}

/**
 * Output path readiness
 */
export interface OutputPathReadiness extends ReadinessSection {
  targetFocusListIdentified: boolean;
  targetFocusListName?: string;
  decisionRightsAssigned: boolean;
  decisionMaker?: string;
  leadershipReviewScheduled: boolean;
  reviewDate?: Date;
}

/**
 * Full cycle readiness check
 */
export interface CycleReadinessCheck {
  cycleId: string;
  cycleName: string;
  currentPhase: string;
  
  // Three main sections
  participantsComplete: ParticipantsReadiness;
  clarityPlanComplete: ClarityPlanReadiness;
  outputPathComplete: OutputPathReadiness;
  
  // Overall status
  overallReady: boolean;
  overallScore: number;  // 0-100
  blockers: string[];
  warnings: string[];
  
  // Next actions
  nextActions: Array<{
    priority: 'high' | 'medium' | 'low';
    action: string;
    assignee?: string;
    dueDate?: Date;
  }>;
  
  // Metadata
  checkedAt: Date;
}

/**
 * Cycle data input for validation
 */
export interface CycleData {
  id: string;
  name: string;
  currentPhase: string;
  
  // Participants
  facilitatorId?: string;
  facilitatorName?: string;
  participantIds: string[];
  stakeholderIds: string[];
  roleAssignments: Array<{
    userId: string;
    role: string;
  }>;
  
  // Comparison set
  comparisonSetItemIds: string[];
  itemReadinessMap: Record<string, {
    hasRequiredValues: boolean;
    missingAttributes: string[];
  }>;
  
  // Criteria
  criteriaIds: string[];
  criteriaWeights: Record<string, number>;
  
  // Sessions
  sessions: Array<{
    id: string;
    name: string;
    scheduledFor?: Date;
    toolId?: string;
    status: string;
  }>;
  
  // Output
  targetFocusListId?: string;
  targetFocusListName?: string;
  decisionMakerId?: string;
  decisionMakerName?: string;
  leadershipReviewDate?: Date;
}

/**
 * Role requirements per phase
 */
const ROLE_REQUIREMENTS: Record<string, Array<{ role: string; required: number }>> = {
  DECIDE: [
    { role: 'facilitator', required: 1 },
  ],
  ENGAGE: [
    { role: 'facilitator', required: 1 },
    { role: 'participant', required: 2 },
  ],
  GATHER: [
    { role: 'facilitator', required: 1 },
    { role: 'steward', required: 1 },
  ],
  ARRANGE: [
    { role: 'facilitator', required: 1 },
    { role: 'participant', required: 2 },
  ],
  PRIORITIZE: [
    { role: 'facilitator', required: 1 },
    { role: 'list_owner', required: 1 },
  ],
};

/**
 * Check participants readiness
 */
function checkParticipantsReadiness(cycle: CycleData): ParticipantsReadiness {
  const items: ReadinessItem[] = [];
  const gaps: string[] = [];
  
  // Check facilitator
  const facilitatorAssigned = !!cycle.facilitatorId;
  items.push({
    id: 'facilitator',
    label: 'Facilitator Assigned',
    status: facilitatorAssigned ? 'complete' : 'incomplete',
    message: facilitatorAssigned ? cycle.facilitatorName : 'No facilitator assigned',
    actionUrl: '/prioritization/settings/roles',
  });
  if (!facilitatorAssigned) gaps.push('Assign a facilitator');
  
  // Check stakeholders
  const criticalStakeholdersIdentified = cycle.stakeholderIds.length >= 1;
  items.push({
    id: 'stakeholders',
    label: 'Critical Stakeholders Identified',
    status: criticalStakeholdersIdentified ? 'complete' : 'warning',
    message: `${cycle.stakeholderIds.length} stakeholder(s) identified`,
  });
  if (!criticalStakeholdersIdentified) gaps.push('Identify critical stakeholders');
  
  // Check participant count
  const hasEnoughParticipants = cycle.participantIds.length >= 2;
  items.push({
    id: 'participants',
    label: 'Participants Enrolled',
    status: hasEnoughParticipants ? 'complete' : 'incomplete',
    message: `${cycle.participantIds.length} participant(s) enrolled`,
  });
  if (!hasEnoughParticipants) gaps.push('Enroll at least 2 participants');
  
  // Check role assignments
  const requirements = ROLE_REQUIREMENTS[cycle.currentPhase] || [];
  const roleAssignments = requirements.map(req => {
    const assigned = cycle.roleAssignments.filter(ra => ra.role === req.role).length;
    return {
      role: req.role,
      assignedCount: assigned,
      requiredCount: req.required,
    };
  });
  
  for (const ra of roleAssignments) {
    const complete = ra.assignedCount >= ra.requiredCount;
    items.push({
      id: `role_${ra.role}`,
      label: `${ra.role} Role`,
      status: complete ? 'complete' : 'incomplete',
      message: `${ra.assignedCount}/${ra.requiredCount} assigned`,
    });
    if (!complete) gaps.push(`Assign ${ra.requiredCount - ra.assignedCount} more ${ra.role}(s)`);
  }
  
  return {
    ready: gaps.length === 0,
    items,
    gaps,
    facilitatorAssigned,
    facilitatorName: cycle.facilitatorName,
    criticalStakeholdersIdentified,
    stakeholderCount: cycle.stakeholderIds.length,
    participantCount: cycle.participantIds.length,
    roleAssignments,
  };
}

/**
 * Check clarity plan readiness
 */
function checkClarityPlanReadiness(cycle: CycleData): ClarityPlanReadiness {
  const items: ReadinessItem[] = [];
  const gaps: string[] = [];
  
  // Comparison set
  const comparisonSetDefined = cycle.comparisonSetItemIds.length > 0;
  items.push({
    id: 'comparison_set',
    label: 'Items Selected for Ranking',
    status: comparisonSetDefined ? 'complete' : 'incomplete',
    message: `${cycle.comparisonSetItemIds.length} item(s) selected`,
    actionUrl: '/prioritization/cycles/' + cycle.id + '/gather',
  });
  if (!comparisonSetDefined) gaps.push('Add items to cycle');
  
  // Criteria
  const criteriaSelected = cycle.criteriaIds.length >= 2;
  items.push({
    id: 'criteria',
    label: 'Criteria Selected',
    status: criteriaSelected ? 'complete' : 'incomplete',
    message: `${cycle.criteriaIds.length} criteria selected`,
  });
  if (!criteriaSelected) gaps.push('Select at least 2 criteria');
  
  // Weights
  const totalWeight = Object.values(cycle.criteriaWeights).reduce((a, b) => a + b, 0);
  const weightsComplete = Math.abs(totalWeight - 100) < 0.01;
  const weightsAssigned = Object.keys(cycle.criteriaWeights).length > 0;
  items.push({
    id: 'weights',
    label: 'Criteria Weights Assigned',
    status: weightsComplete ? 'complete' : weightsAssigned ? 'warning' : 'incomplete',
    message: weightsComplete ? 'Weights sum to 100%' : `Weights sum to ${totalWeight}%`,
  });
  if (!weightsComplete) gaps.push('Adjust weights to sum to 100%');
  
  // Sessions
  const sessionsScheduled = cycle.sessions.some(s => s.scheduledFor);
  items.push({
    id: 'sessions',
    label: 'Sessions Scheduled',
    status: sessionsScheduled ? 'complete' : 'incomplete',
    message: `${cycle.sessions.length} session(s) created`,
  });
  if (!sessionsScheduled) gaps.push('Schedule at least one session');
  
  // Tools
  const toolsSelected = cycle.sessions.some(s => s.toolId);
  items.push({
    id: 'tools',
    label: 'Tools Selected',
    status: toolsSelected ? 'complete' : 'incomplete',
    message: toolsSelected ? 'Tool(s) configured' : 'No tools selected',
  });
  if (!toolsSelected) gaps.push('Select prioritization tools');
  
  // Item readiness
  let itemsReady = 0;
  let itemsWithMissingValues = 0;
  
  for (const itemId of cycle.comparisonSetItemIds) {
    const readiness = cycle.itemReadinessMap[itemId];
    if (readiness?.hasRequiredValues) {
      itemsReady++;
    } else {
      itemsWithMissingValues++;
    }
  }
  
  const allItemsReady = itemsWithMissingValues === 0;
  items.push({
    id: 'items_ready',
    label: 'Items Meet Prerequisites',
    status: allItemsReady ? 'complete' : 'warning',
    message: `${itemsReady}/${cycle.comparisonSetItemIds.length} items ready`,
  });
  if (itemsWithMissingValues > 0) {
    gaps.push(`${itemsWithMissingValues} item(s) need attribute values`);
  }
  
  return {
    ready: gaps.length === 0,
    items,
    gaps,
    comparisonSetDefined,
    comparisonSetSize: cycle.comparisonSetItemIds.length,
    criteriaSelected,
    criteriaCount: cycle.criteriaIds.length,
    weightsAssigned,
    weightsComplete,
    sessionsScheduled,
    sessionCount: cycle.sessions.length,
    toolsSelected,
    toolCount: cycle.sessions.filter(s => s.toolId).length,
    itemsWithMissingValues,
    itemsReady,
    totalItems: cycle.comparisonSetItemIds.length,
  };
}

/**
 * Check output path readiness
 */
function checkOutputPathReadiness(cycle: CycleData): OutputPathReadiness {
  const items: ReadinessItem[] = [];
  const gaps: string[] = [];
  
  // Target Item Pool
  const targetFocusListIdentified = !!cycle.targetFocusListId;
  items.push({
    id: 'target_list',
    label: 'Target Item Pool Identified',
    status: targetFocusListIdentified ? 'complete' : 'incomplete',
    message: targetFocusListIdentified ? cycle.targetFocusListName : 'No target pool selected',
  });
  if (!targetFocusListIdentified) gaps.push('Select target Item Pool');
  
  // Decision rights
  const decisionRightsAssigned = !!cycle.decisionMakerId;
  items.push({
    id: 'decision_rights',
    label: 'Decision Rights Assigned',
    status: decisionRightsAssigned ? 'complete' : 'warning',
    message: decisionRightsAssigned ? cycle.decisionMakerName : 'No decision maker assigned',
  });
  if (!decisionRightsAssigned) gaps.push('Assign decision maker');
  
  // Leadership review
  const leadershipReviewScheduled = !!cycle.leadershipReviewDate;
  items.push({
    id: 'leadership_review',
    label: 'Leadership Review Scheduled',
    status: leadershipReviewScheduled ? 'complete' : 'warning',
    message: leadershipReviewScheduled 
      ? `Scheduled for ${cycle.leadershipReviewDate?.toLocaleDateString()}`
      : 'Not scheduled',
  });
  if (!leadershipReviewScheduled) gaps.push('Schedule leadership review');
  
  return {
    ready: gaps.filter(g => !g.includes('warning')).length === 0,
    items,
    gaps,
    targetFocusListIdentified,
    targetFocusListName: cycle.targetFocusListName,
    decisionRightsAssigned,
    decisionMaker: cycle.decisionMakerName,
    leadershipReviewScheduled,
    reviewDate: cycle.leadershipReviewDate,
  };
}

/**
 * Compute full cycle readiness
 */
export function computeCycleReadiness(cycle: CycleData): CycleReadinessCheck {
  // Skip detailed checks if feature is disabled
  if (!isFeatureEnabled(FEATURE_FLAGS.CYCLE_READINESS)) {
    return {
      cycleId: cycle.id,
      cycleName: cycle.name,
      currentPhase: cycle.currentPhase,
      participantsComplete: {
        ready: true,
        items: [],
        gaps: [],
        facilitatorAssigned: true,
        criticalStakeholdersIdentified: true,
        stakeholderCount: 0,
        participantCount: 0,
        roleAssignments: [],
      },
      clarityPlanComplete: {
        ready: true,
        items: [],
        gaps: [],
        comparisonSetDefined: true,
        comparisonSetSize: 0,
        criteriaSelected: true,
        criteriaCount: 0,
        weightsAssigned: true,
        weightsComplete: true,
        sessionsScheduled: true,
        sessionCount: 0,
        toolsSelected: true,
        toolCount: 0,
        itemsWithMissingValues: 0,
        itemsReady: 0,
        totalItems: 0,
      },
      outputPathComplete: {
        ready: true,
        items: [],
        gaps: [],
        targetFocusListIdentified: true,
        decisionRightsAssigned: true,
        leadershipReviewScheduled: true,
      },
      overallReady: true,
      overallScore: 100,
      blockers: [],
      warnings: [],
      nextActions: [],
      checkedAt: new Date(),
    };
  }
  
  const participantsComplete = checkParticipantsReadiness(cycle);
  const clarityPlanComplete = checkClarityPlanReadiness(cycle);
  const outputPathComplete = checkOutputPathReadiness(cycle);
  
  // Collect all gaps as blockers and warnings
  const blockers: string[] = [
    ...participantsComplete.gaps.filter(g => !g.includes('stakeholder')),
    ...clarityPlanComplete.gaps.filter(g => !g.includes('item(s) need')),
    ...outputPathComplete.gaps.filter(g => !g.includes('Schedule')),
  ];
  
  const warnings: string[] = [
    ...participantsComplete.gaps.filter(g => g.includes('stakeholder')),
    ...clarityPlanComplete.gaps.filter(g => g.includes('item(s) need')),
    ...outputPathComplete.gaps.filter(g => g.includes('Schedule')),
  ];
  
  // Calculate overall score
  const sectionScores = [
    participantsComplete.ready ? 100 : (participantsComplete.items.filter(i => i.status === 'complete').length / participantsComplete.items.length) * 100,
    clarityPlanComplete.ready ? 100 : (clarityPlanComplete.items.filter(i => i.status === 'complete').length / clarityPlanComplete.items.length) * 100,
    outputPathComplete.ready ? 100 : (outputPathComplete.items.filter(i => i.status === 'complete').length / outputPathComplete.items.length) * 100,
  ];
  const overallScore = Math.round(sectionScores.reduce((a, b) => a + b, 0) / 3);
  
  // Generate next actions
  const nextActions: CycleReadinessCheck['nextActions'] = [];
  
  for (const blocker of blockers.slice(0, 3)) {
    nextActions.push({
      priority: 'high',
      action: blocker,
    });
  }
  
  for (const warning of warnings.slice(0, 2)) {
    nextActions.push({
      priority: 'medium',
      action: warning,
    });
  }
  
  return {
    cycleId: cycle.id,
    cycleName: cycle.name,
    currentPhase: cycle.currentPhase,
    participantsComplete,
    clarityPlanComplete,
    outputPathComplete,
    overallReady: blockers.length === 0,
    overallScore,
    blockers,
    warnings,
    nextActions,
    checkedAt: new Date(),
  };
}

/**
 * Check if cycle can advance to next phase
 */
export function canAdvancePhase(cycle: CycleData): { canAdvance: boolean; reasons: string[] } {
  const readiness = computeCycleReadiness(cycle);
  
  // Phase-specific requirements
  const phaseRequirements: Record<string, (r: CycleReadinessCheck) => string[]> = {
    DECIDE: (r) => {
      const issues: string[] = [];
      if (!r.participantsComplete.facilitatorAssigned) issues.push('Facilitator required');
      return issues;
    },
    ENGAGE: (r) => {
      const issues: string[] = [];
      if (r.participantsComplete.participantCount < 2) issues.push('At least 2 participants required');
      return issues;
    },
    GATHER: (r) => {
      const issues: string[] = [];
      if (!r.clarityPlanComplete.comparisonSetDefined) issues.push('Items required for ranking');
      return issues;
    },
    ARRANGE: (r) => {
      const issues: string[] = [];
      if (!r.clarityPlanComplete.criteriaSelected) issues.push('Criteria must be selected');
      if (!r.clarityPlanComplete.sessionsScheduled) issues.push('At least one session required');
      return issues;
    },
    PRIORITIZE: (r) => {
      const issues: string[] = [];
      if (!r.outputPathComplete.targetFocusListIdentified) issues.push('Target Item Pool required');
      return issues;
    },
  };
  
  const checkFn = phaseRequirements[cycle.currentPhase];
  const reasons = checkFn ? checkFn(readiness) : [];
  
  return {
    canAdvance: reasons.length === 0,
    reasons,
  };
}
